import { Button, Icon, Modal, ModalBody, Text } from '@antoree/ant-ui';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import {
  GiftIcon,
  InfoIcon,
  AlertTriangleIcon,
  FlameIcon,
  MegaphoneIcon,
} from '../../containers/Chat/utils';
import { useLocation } from 'react-router-dom';
import { getActiveBanners, IBanner } from 'services';

const BannerContainer = styled.div<{
  backgroundColor?: string;
  textColor?: string;
}>`
  background-color: ${props => props.backgroundColor || '#fff'};
  color: ${props => props.textColor || '#333'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: flex-start;
  gap: 16px;
  position: relative;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`;

const IconWrapper = styled.div`
  font-size: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
`;

const ContentWrapper = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 4px 0;
`;

const Message = styled.p`
  font-size: 14px;
  margin: 0 0 12px 0;
  line-height: 1.5;
  opacity: 0.9;
`;

const ActionButton = styled.a<{ buttonColor?: string; textColor?: string }>`
  display: inline-block;
  background-color: ${props => props.buttonColor || '#0aa263'};
  color: ${props => props.textColor || '#fff'};
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.5;
  font-size: 18px;
  padding: 4px;

  &:hover {
    opacity: 1;
  }
`;

interface Props {
  canShowPopup?: boolean;
}

const DynamicBanner: React.FC<Props> = ({ canShowPopup = true }) => {
  const location = useLocation();
  const [banners, setBanners] = useState<IBanner[]>([]);
  const [visibleBanners, setVisibleBanners] = useState<IBanner[]>([]);
  const [dismissedBanners, setDismissedBanners] = useState<Set<string>>(
    new Set(),
  );
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [currentInlineIndex, setCurrentInlineIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasShownModalRef = React.useRef(false);

  // Fetch banners on mount
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const bannersData = await getActiveBanners('all');
        setBanners(bannersData);
      } catch (error) {
        console.error('Failed to fetch banners', error);
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    // Filter banners based on current time and persistence/dismissal logic
    const now = moment();
    const isDev = process.env.NODE_ENV === 'development';

    const filtered = (banners || []).filter(banner => {
      // Skip dismissed banners
      if (dismissedBanners.has(banner.id)) {
        return false;
      }

      let isValid = true;
      let failReason = '';

      const start = moment(banner.startAt);
      const end = moment(banner.endAt);

      // Check if banner is active within the date range
      if (!now.isBetween(start, end)) {
        isValid = false;
        failReason = 'date_range';
      }

      // Only show banners for 'all' or 'teachers' audience
      if (
        isValid &&
        banner.targetAudience &&
        banner.targetAudience !== 'all' &&
        banner.targetAudience !== 'teachers'
      ) {
        isValid = false;
        failReason = 'audience';
      }

      if (isValid) {
        return true;
      }

      if (isDev) {
        (banner as any)._isDebug = true;
        return true;
      }

      return false;
    });

    // Sort by priority (descending)
    filtered.sort((a, b) => b.priority - a.priority);

    setVisibleBanners(filtered);
  }, [banners, dismissedBanners]);

  // Separate useEffect to show modal when banners are loaded (only once)
  // Separate banners based on displayMode
  const popupBanners = useMemo(
    () =>
      visibleBanners.filter(b => {
        const mode = b.metadata?.displayMode;
        if (mode === 'popup') return true;
        // Fallback for legacy banners or missing displayMode
        if (!mode && b.imageUrl) return true;
        return false;
      }),
    [visibleBanners],
  );

  const inlineBanners = useMemo(
    () =>
      visibleBanners.filter(b => {
        const mode = b.metadata?.displayMode;
        if (mode === 'banner' || mode === 'inline') return true;
        // Fallback for legacy banners or missing displayMode
        if (!mode && !b.imageUrl) return true;
        return false;
      }),
    [visibleBanners],
  );

  useEffect(() => {
    if (
      canShowPopup &&
      visibleBanners.length > 0 &&
      !hasShownModalRef.current
    ) {
      if (popupBanners.length > 0) {
        setCurrentModalIndex(0);
        setIsModalOpen(true);
        hasShownModalRef.current = true;
      }
    }
  }, [visibleBanners, popupBanners, canShowPopup]);

  useEffect(() => {
    if (inlineBanners.length > 1) {
      const timer = setInterval(() => {
        setCurrentInlineIndex(prev => (prev + 1) % inlineBanners.length);
      }, 5000); // 5 seconds
      return () => clearInterval(timer);
    }
  }, [inlineBanners]);

  const handleDismiss = (id: string) => {
    // Add to dismissed set - will be reset on route change
    setDismissedBanners(prev => new Set(prev).add(id));
  };

  const handleCloseModal = () => {
    const popupBanners = visibleBanners.filter(b => {
      const mode = b.metadata?.displayMode;
      return mode === 'popup' || (!mode && b.imageUrl);
    });

    // Check if there's a next image banner to show
    if (currentModalIndex < popupBanners.length - 1) {
      setCurrentModalIndex(currentModalIndex + 1);
    } else {
      // No more image banners, close modal
      setIsModalOpen(false);
      setCurrentModalIndex(0);
    }
  };

  const renderIcon = (iconName?: string) => {
    switch (iconName) {
      case 'gift':
        return <GiftIcon size={24} />;
      case 'info':
        return <InfoIcon size={24} />;
      case 'warning':
        return <AlertTriangleIcon size={24} color="#f59e0b" />;
      case 'fire':
        return <FlameIcon size={24} />;
      default:
        return <MegaphoneIcon size={24} />;
    }
  };

  // Helper function to get localized text with fallback
  const getLocalizedText = (
    i18nData: any,
    legacyText: string,
    locale: string = 'vi',
  ): string => {
    if (i18nData && typeof i18nData === 'object' && i18nData[locale]) {
      return i18nData[locale];
    }
    return legacyText;
  };

  // Get current locale (you can get this from react-intl context if needed)
  const currentLocale = 'vi'; // Default to Vietnamese, can be dynamic

  const currentModalBanner = popupBanners[currentModalIndex];

  if (inlineBanners.length === 0 && !isModalOpen) return null;

  return (
    <>
      {/* Modal for image banners */}
      {isModalOpen && currentModalBanner && (
        <Modal onClose={handleCloseModal}>
          <ModalBody style={{ padding: 0 }}>
            <div style={{ position: 'relative' }}>
              {(currentModalBanner as any)._isDebug && (
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    background: 'red',
                    color: 'white',
                    padding: '4px 8px',
                    zIndex: 9999,
                    fontWeight: 'bold',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  DEV (Inactive)
                </div>
              )}
              <img
                src={currentModalBanner.imageUrl}
                alt={getLocalizedText(
                  currentModalBanner.titleI18n,
                  currentModalBanner.titleLegacy,
                  currentLocale,
                )}
                style={{
                  width: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
              {/* Show action button if banner has actionUrl */}
              {currentModalBanner.actionType === 'link' &&
                currentModalBanner.actionUrl && (
                  <div
                    style={{
                      padding: '16px',
                      textAlign: 'center',
                      backgroundColor: '#fff',
                    }}
                  >
                    <ActionButton
                      href={currentModalBanner.actionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      buttonColor={currentModalBanner.textColor || '#0aa263'}
                      textColor={currentModalBanner.backgroundColor || '#fff'}
                      style={{
                        display: 'inline-block',
                        minWidth: '200px',
                      }}
                    >
                      {getLocalizedText(
                        currentModalBanner.actionLabelI18n,
                        currentModalBanner.actionLabelLegacy || '',
                        currentLocale,
                      ) || (
                        <FormattedMessage
                          id="common.view_details"
                          defaultMessage="View Details"
                        />
                      )}
                    </ActionButton>
                  </div>
                )}
            </div>
          </ModalBody>
        </Modal>
      )}

      {/* Inline banners slider */}
      {inlineBanners.length > 0 && (
        <div style={{ padding: '0 24px', marginTop: '16px' }}>
          <BannerContainer
            key={inlineBanners[currentInlineIndex].id}
            backgroundColor={inlineBanners[currentInlineIndex].backgroundColor}
            textColor={inlineBanners[currentInlineIndex].textColor}
            style={{
              position: 'relative',
              overflow: 'hidden',
              marginBottom: inlineBanners.length > 1 ? '12px' : '24px',
            }}
          >
            {(inlineBanners[currentInlineIndex] as any)._isDebug && (
              <div
                style={{
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  background: 'red',
                  color: 'white',
                  padding: '2px 8px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  borderBottomRightRadius: '4px',
                }}
              >
                DEV
              </div>
            )}
            <IconWrapper>
              {renderIcon(inlineBanners[currentInlineIndex].icon)}
            </IconWrapper>
            <ContentWrapper>
              <Title>
                {getLocalizedText(
                  inlineBanners[currentInlineIndex].titleI18n,
                  inlineBanners[currentInlineIndex].titleLegacy,
                  currentLocale,
                )}
              </Title>
              <Message>
                {getLocalizedText(
                  inlineBanners[currentInlineIndex].messageI18n,
                  inlineBanners[currentInlineIndex].messageLegacy,
                  currentLocale,
                )}
              </Message>
              {inlineBanners[currentInlineIndex].actionType === 'link' &&
                inlineBanners[currentInlineIndex].actionUrl && (
                  <ActionButton
                    href={inlineBanners[currentInlineIndex].actionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    buttonColor={inlineBanners[currentInlineIndex].textColor}
                    textColor={
                      inlineBanners[currentInlineIndex].backgroundColor
                    }
                  >
                    {getLocalizedText(
                      inlineBanners[currentInlineIndex].actionLabelI18n,
                      inlineBanners[currentInlineIndex].actionLabelLegacy || '',
                      currentLocale,
                    ) || (
                      <FormattedMessage
                        id="common.view_details"
                        defaultMessage="View Details"
                      />
                    )}
                  </ActionButton>
                )}
            </ContentWrapper>
            {inlineBanners[currentInlineIndex].isDismissible === 1 && (
              <CloseButton
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDismiss(inlineBanners[currentInlineIndex].id);
                }}
              >
                ✕
              </CloseButton>
            )}
          </BannerContainer>

          {/* Slider dots */}
          {inlineBanners.length > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '12px',
              }}
            >
              {inlineBanners.map((_: any, index: number) => (
                <button
                  key={index}
                  onClick={() => setCurrentInlineIndex(index)}
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor:
                      index === currentInlineIndex ? '#0aa263' : '#ddd',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DynamicBanner;
